import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SelfCheckInService } from '../../services/selfCheckIn.service';
import { UserService } from '../../services/user.service';
import { Subscription, interval } from 'rxjs';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { AppUtility } from '../../common/apputility';

@Component({
  selector: 'app-self-check-in-form',
  templateUrl: './selfCheckInForm.component.html',
  styleUrls: ['./selfCheckInForm.component.css']
})
export class SelfCheckInFormComponent implements OnInit, OnDestroy {
  deviceId: number;
  loading = true;
  loadError: string = '';
  accountId: number;
  deviceType: string = '';
  hostType: string = '';
  residentId: number = null;
  employeeId: number = null;
  hostList: any[] = [];
  selectedHostId: number = null;
  hostSearchTerm: string = '';
  showHostDropdown: boolean = false;
  hostSearchResults: any[] = [];
  selectedHostName: string = '';
  countryList: any[] = [];
  todayDate1: Date;
  imageName: string = '';
  filestring: any;
  logo: any[] = [];
  submitting = false;
  submitted = false;
  submitError: string = '';
  selfGuestId: number = null;
  status: string = '';
  message: string = '';
  qrImage: string = '';
  polling: Subscription = null;
  visitorForm: FormGroup;
  private subscribe: Subscription[] = [];
  isUniversal: boolean = false;
  hostPreset: boolean = false;
  mode: string;

  constructor(
    private route: ActivatedRoute,
    private toaster: ToastrService,
    private selfCheckInService: SelfCheckInService,
    private userService: UserService
  ) {
    this.route.params.subscribe(params => {
      this.deviceId = params['deviceId'];
    });
  }

  ngOnInit(): void {
    this.todayDate1 = new Date();
    this.visitorForm = new FormGroup({
      fullName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      documentType: new FormControl(''),
      documentId: new FormControl('', [Validators.maxLength(30)]),
      contactNo: new FormControl(''),
      dialingCode: new FormControl(''),
      email: new FormControl('', [Validators.maxLength(50)]),
      address: new FormControl('', [Validators.maxLength(100)]),
      nationality: new FormControl('', Validators.required),
      gender: new FormControl(''),
      mode: new FormControl(''),
      purposeOfVisit: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      expectedDate: new FormControl('', Validators.required),
      expectedVehicleNo: new FormControl('', [Validators.pattern('[a-zA-Z0-9 ]*'), Validators.maxLength(20)]),
      expectedVehicleModel: new FormControl('', [Validators.pattern('[a-zA-Z0-9 ]*'), Validators.maxLength(20)])
    });

    if (!this.deviceId) {
      this.loading = false;
      this.loadError = 'Invalid QR code.';
      return;
    }

    this.validateDevice();
    this.getCountryList();
  }

  validateDevice(): void {
    if (!this.deviceId) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.subscribe.push(
      this.selfCheckInService.validateDevice(this.deviceId.toString()).subscribe({
        next: (data: any) => {
          this.loading = false;
          const wrapper = JSON.parse(JSON.stringify(data));
          this.accountId = wrapper.accountId;
          this.deviceType = wrapper.type;
          this.hostType = wrapper.hostType || '';
          this.residentId = wrapper.residentId || null;
          this.employeeId = wrapper.employeeId || null;
          this.isUniversal = this.deviceType === 'UNIVERSAL';
          this.hostPreset = this.deviceType === 'NON_UNIVERSAL';
          if (this.isUniversal) {
            this.loadHosts();
          }
        },
        error: (err) => {
          this.loading = false;
          this.loadError = err.error?.message || 'This QR code is not valid or has expired.';
        }
      })
    );
  }

  loadHosts(): void {
    this.subscribe.push(
      this.selfCheckInService.getAllHosts(this.accountId).subscribe({
        next: (data: any) => { this.hostList = data || []; },
        error: (err) => { console.error('Error loading hosts:', err); this.hostList = []; }
      })
    );
  }

  onHostSearchChange(searchTerm: string): void {
    this.hostSearchTerm = searchTerm;
    this.selectedHostId = null;
    this.selectedHostName = '';
    if (!searchTerm || searchTerm.trim().length === 0 || !this.accountId) {
      this.hostSearchResults = [];
      this.showHostDropdown = false;
      return;
    }
    this.subscribe.push(
      this.selfCheckInService.searchHost(this.accountId, searchTerm).subscribe({
        next: (data: any) => {
          this.hostSearchResults = data || [];
          this.showHostDropdown = true;
        },
        error: (err) => {
          this.hostSearchResults = [];
          this.showHostDropdown = true;
          console.error('Error searching hosts:', err);
        }
      })
    );
  }

  onHostFocus(): void {
    if (this.hostSearchTerm && this.hostSearchResults.length > 0) {
      this.showHostDropdown = true;
    }
  }

  onHostBlur(): void {
    setTimeout(() => {
      this.showHostDropdown = false;
    }, 200);
  }

  // Prevents the input's blur handler from closing the dropdown
  // before the click/tap on a host item can register.
  onHostSelectMouseDown(event: MouseEvent | TouchEvent, host: any): void {
    event.preventDefault();
    this.selectHost(host);
  }

  selectHost(host: any): void {
    this.selectedHostId = host.id;
    this.selectedHostName = host.fullName;
    this.hostType = host.hostType;
    this.showHostDropdown = false;
    this.hostSearchTerm = host.fullName;
  }

  onChangeMode(event: any): void {
    const mode = event.target.value;
    this.mode = mode;
    this.visitorForm.patchValue({ mode: mode });
  }

  getCountryList(): void {
    this.subscribe.push(
      this.userService.getCountryList('').subscribe({
        next: (data: any) => { this.countryList = data; },
        error: (err) => { console.error('Error loading countries:', err); }
      })
    );
  }

  onFileChange(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const fextension = file.name.split('.');
      if (fextension.length < 2) {
        this.toaster.error('Please select valid file');
        return;
      }
      if (fextension[1] !== 'png' && fextension[1] !== 'jpg' && fextension[1] !== 'jpeg') {
        this.toaster.error('Image type should be .png or .jpg or .jpeg');
        return;
      }
      if (file.size > 1000000) {
        this.toaster.error('Image size should be less than 1 MB');
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.filestring = reader.result;
        this.imageName = this.filestring;
      };
    }
  }

  submitSelfCheckIn(): void {
    if (this.visitorForm.invalid) {
      this.toaster.error('Please fill in all required fields.');
      return;
    }
    if (this.isUniversal && !this.selectedHostId) {
      this.toaster.error('Please select who you are visiting.');
      return;
    }
    const formValue = this.visitorForm.value;
    const details: any = {
      deviceId: this.deviceId,
      accountId: this.accountId,
      ...formValue,
      mode: formValue.mode || 'WALK_IN'
    };
    if (this.isUniversal) {
      if (this.hostType === 'RESIDENTIAL') {
        details.residentId = this.selectedHostId;
        details.employeeId = null;
      } else {
        details.employeeId = this.selectedHostId;
        details.residentId = null;
      }
    } else {
      details.residentId = this.residentId;
      details.employeeId = this.employeeId;
    }

    if (details.email !== null && details.email !== undefined && details.email.trim() === '') {
      details.email = null;
    }

    if (this.filestring !== undefined && this.filestring !== '') {
      this.logo = this.filestring.split(',');
      if (this.logo.length > 1) {
        details.image = this.logo[1];
      }
    } else {
      details.image = null;
    }

    if (!AppUtility.isEmptyString(details.mode) && details.mode === 'Drive-In') {
      if (AppUtility.isEmptyString(details.expectedVehicleNo)) {
        details.expectedVehicleNo = null;
      }
    } else {
      details.expectedVehicleNo = null;
      details.expectedVehicleModel = null;
    }

    const d = this.visitorForm.value.expectedDate;
    details.expectedDate = new Date(d);
    if (details.expectedDate <= new Date()) {
      this.toaster.error('Expected date should be greater than current date');
      return;
    }

    this.submitting = true;
    this.subscribe.push(
      this.selfCheckInService.submitSelfCheckIn(details).subscribe({
        next: (response: any) => {
          this.submitting = false;
          this.submitted = true;
          this.selfGuestId = response?.selfGuestId || response?.id || null;
          this.status = response?.status || 'PENDING';
          this.message = response?.message || 'Your request is pending approval. Please wait for the host to accept.';
          this.toaster.success('Submitted! Waiting for host approval.');
          if (this.selfGuestId) {
            this.startPolling();
          }
        },
        error: (err) => {
          this.submitting = false;
          this.submitError = err.error?.message || 'Failed to submit. Please try again.';
          this.toaster.error(this.submitError);
        }
      })
    );
  }

  startPolling(): void {
    this.polling = interval(5000).subscribe(() => {
      this.selfCheckInService.getStatus(this.selfGuestId).subscribe({
        next: (data: any) => {
          this.status = data.status || this.status;
          this.message = data.message || this.message;
          if (data.qrCode) {
            this.qrImage = 'data:image/png;base64,' + data.qrCode;
          }
          if (this.status === 'ACCEPTED' || this.status === 'REJECTED') {
            this.polling.unsubscribe();
          }
        },
        error: (err) => { console.error('Error polling status:', err); }
      });
    });
  }

  ngOnDestroy(): void {
    this.subscribe.forEach(sub => sub.unsubscribe());
    if (this.polling) {
      this.polling.unsubscribe();
    }
  }
}